import { useCallback, useEffect, useRef, useState } from "react";
import { BlockButton } from "../Button/BlockButton";
import { Icon } from "../Icon/Icon";
import { RangeInput } from "../RangeInput/RangeInput";
export const PtzCamera = ({
	className = "",
	uploadUrl = "",
	isFocusing = false,
	isPC = false,
}) => {
	// Camera control states
	const [zoom, setZoom] = useState(1);
	const [pan, setPan] = useState(0);
	const [tilt, setTilt] = useState(0);
	const [isStreaming, setIsStreaming] = useState(false);
	const [isRecording, setIsRecording] = useState(false);

	// Camera states
	const [stream, setStream] = useState(null);
	const [track, setTrack] = useState(null);
	const [capabilities, setCapabilities] = useState(null);
	const [_constraints, setConstraints] = useState(null);
	const [facingMode, setFacingMode] = useState("user");
	const [_availableCameras, setAvailableCameras] = useState([]);
	const [error, setError] = useState(null);

	// Resolution and latency states
	const [resolution, setResolution] = useState({ width: 0, height: 0 });
	const [latency, setLatency] = useState(null);

	// Control states
	const [isDragging, setIsDragging] = useState(false);
	const [dragStart, setDragStart] = useState({ x: 0, y: 0 });

	const videoRef = useRef(null);
	const fileInputRef = useRef(null);

	// Helper function to get resolution label
	const getResolutionLabel = (width, height) => {
		if (width === 0 || height === 0) return "N/A";

		// Common resolution labels based on width and height
		if (width >= 3840 && height >= 2160) return "4K UHD";
		if (width >= 2560 && height >= 1440) return "2K QHD";
		if (width >= 1920 && height >= 1080) return "Full HD";
		if (width >= 1280 && height >= 720) return "HD";
		if (width >= 854 && height >= 480) return "SD";
		if (width >= 640 && height >= 480) return "VGA";

		// Fallback to pixel dimensions
		return `${width}x${height}`;
	};

	// Get available cameras
	const getAvailableCameras = useCallback(async () => {
		try {
			const devices = await navigator.mediaDevices.enumerateDevices();
			const videoDevices = devices.filter(
				(device) => device.kind === "videoinput",
			);
			setAvailableCameras(videoDevices);
			console.log(
				"📹 Available cameras:",
				videoDevices.map((d) => d.label || `Camera ${d.deviceId.slice(0, 8)}`),
			);
			return videoDevices;
		} catch (err) {
			console.error("❌ Error getting camera list:", err);
			return [];
		}
	}, []);

	// Get camera capabilities and constraints
	const getCameraCapabilities = useCallback(async (videoTrack) => {
		try {
			const caps = videoTrack.getCapabilities();
			const settings = videoTrack.getSettings();

			console.log("📹 Camera Capabilities:", caps);
			console.log("⚙️ Current Settings:", settings);

			setCapabilities(caps);
			setConstraints(settings);

			// Set resolution from settings
			if (settings.width && settings.height) {
				setResolution({ width: settings.width, height: settings.height });
				console.log(`📐 Resolution: ${settings.width}x${settings.height}`);
			}

			// Initialize zoom, pan, tilt from current settings
			if (settings.zoom !== undefined) setZoom(settings.zoom);
			if (settings.pan !== undefined) setPan(settings.pan);
			if (settings.tilt !== undefined) setTilt(settings.tilt);

			return caps;
		} catch (err) {
			console.error("❌ Error getting camera capabilities:", err);
			setError("Unable to get camera capabilities");
			return null;
		}
	}, []);

	// Apply camera constraints
	const applyCameraConstraints = useCallback(
		async (newConstraints) => {
			if (!track) {
				console.warn("⚠️ No camera track available");
				return false;
			}

			try {
				await track.applyConstraints(newConstraints);
				console.log("✅ Applied constraints:", newConstraints);

				// Update current settings
				const newSettings = track.getSettings();
				setConstraints(newSettings);

				return true;
			} catch (err) {
				console.error("❌ Error applying constraints:", err);
				setError(`Constraint error: ${err.message}`);
				return false;
			}
		},
		[track],
	);

	// Start camera stream
	const startCamera = async (requestedFacingMode = facingMode) => {
		try {
			setError(null);

			const constraints = {
				video: {
					width: { ideal: 1920, min: 640 },
					height: { ideal: 1080, min: 480 },
					frameRate: { ideal: 30, min: 15 },
					facingMode: { ideal: requestedFacingMode },
					// Request PTZ capabilities if available
					pan: true,
					tilt: true,
					zoom: true,
				},
				audio: false,
			};

			console.log("🎥 Requesting camera access...");

			const mediaStream =
				await navigator.mediaDevices.getUserMedia(constraints);
			const videoTrack = mediaStream.getVideoTracks()[0];
			console.log("🎬 Media Stream obtained:", mediaStream.getVideoTracks());
			console.log("✅ Camera access granted");

			setStream(mediaStream);
			setTrack(videoTrack);
			setIsStreaming(true);

			setTimeout(async () => {
				if (videoRef.current) {
					console.log("📺 Setting video source...");
					videoRef.current.srcObject = mediaStream;

					// Multiple fallback strategies for video playback
					try {
						// Strategy 1: Direct play
						await videoRef.current.play();
						console.log("✅ Video playing directly");
					} catch (playError) {
						console.warn(
							"⚠️ Direct play failed, trying alternatives:",
							playError.message,
						);

						// Strategy 2: Set video properties and retry
						videoRef.current.muted = true;
						videoRef.current.playsInline = true;
						videoRef.current.autoplay = true;

						try {
							await videoRef.current.play();
							console.log("✅ Video playing with properties set");
						} catch (retryError) {
							console.warn(
								"⚠️ Retry with properties failed:",
								retryError.message,
							);

							// Strategy 3: Wait for metadata and try again
							await new Promise((resolve) => {
								videoRef.current.addEventListener("loadedmetadata", resolve, {
									once: true,
								});
							});

							try {
								await videoRef.current.play();
								console.log("✅ Video playing after metadata loaded");
							} catch (finalError) {
								console.error(
									"❌ All play strategies failed:",
									finalError.message,
								);
								// Continue anyway - sometimes video works even if play() throws
							}
						}
					}
				}
			}, 100);

			// Get camera capabilities
			await getCameraCapabilities(videoTrack);
			// Get available cameras for switching
			await getAvailableCameras();
			console.log("🎥 Camera started successfully");
		} catch (err) {
			console.error("❌ Error accessing camera:", err);
			setError(`Camera access error: ${err.message}`);
			setIsStreaming(false);
		}
	};

	// Switch camera facing mode
	const _switchCamera = async () => {
		const newFacingMode = facingMode === "user" ? "environment" : "user";
		console.log(`🔄 Switching camera from ${facingMode} to ${newFacingMode}`);

		stopCamera();

		// Wait a moment for cleanup
		setTimeout(() => {
			// Start with new facing mode
			setFacingMode(newFacingMode);
			startCamera(newFacingMode);
		}, 500);
	};

	// Switch to specific camera device
	const _switchToCamera = async (deviceId) => {
		console.log(`📹 Switching to camera: ${deviceId}`);

		stopCamera();

		// Wait a moment for cleanup
		setTimeout(async () => {
			try {
				const constraints = {
					video: {
						deviceId: { exact: deviceId },
						width: { ideal: 1280, min: 640 },
						height: { ideal: 720, min: 480 },
						frameRate: { ideal: 30, min: 15 },
					},
					audio: false,
				};

				const mediaStream =
					await navigator.mediaDevices.getUserMedia(constraints);
				const videoTrack = mediaStream.getVideoTracks()[0];
				setTimeout(async () => {
					if (videoRef.current) {
						videoRef.current.srcObject = mediaStream;
						videoRef.current.muted = true;
						videoRef.current.playsInline = true;
						await videoRef.current.play();
					}
				}, 100);

				setStream(mediaStream);
				setTrack(videoTrack);
				setIsStreaming(true);
				await getCameraCapabilities(videoTrack);

				console.log("✅ Switched to specific camera successfully");
			} catch (err) {
				console.error("❌ Error switching camera:", err);
				setError(`Failed to switch camera: ${err.message}`);
			}
		}, 500);
	};

	// Stop camera stream
	const stopCamera = useCallback(() => {
		console.log("🛑 Stopping camera...");

		if (stream) {
			console.log("📴 Stopping all tracks:", stream.getTracks().length);
			stream.getTracks().forEach((track) => {
				console.log("Stopping track:", track.kind, track.label);
				track.stop();
			});
			setStream(null);
			setTrack(null);
			setCapabilities(null);
			setConstraints(null);
		}

		if (videoRef.current) {
			videoRef.current.srcObject = null;
		}

		setIsStreaming(false);
		setError(null);
		console.log("⏹️ Camera stopped");
	}, [stream]);

	const stopRecord = () => {
		if (mediaRecorderRef.current) {
			mediaRecorderRef.current.stop();
			setIsRecording(false);
			console.log("⏹️ Recording stopped");
		}
	};

	// Handle focus changes - disconnect camera when out of focus
	useEffect(() => {
		if (!isFocusing) {
			console.log("📴 Component lost focus");
			if (isRecording) {
				console.log("⏹️ Stopping recording due to focus loss");
				stopRecord();
			}
			if (isStreaming) {
				console.log("📴 Stopping camera due to focus loss");
				stopCamera();
			}
		} else {
			if (!isStreaming) {
				startCamera();
			}
		}
	}, [
		isFocusing,
		isStreaming,
		isRecording,
		stopCamera,
		startCamera,
		stopRecord,
	]);

	// Handle zoom control
	const handleZoomChange = async (newZoom) => {
		if (!capabilities?.zoom) {
			console.warn("⚠️ Zoom not supported by this camera");
			setError("Zoom not supported by this camera");
			return;
		}

		const clampedZoom = Math.max(
			capabilities.zoom.min || 1,
			Math.min(capabilities.zoom.max || 5, newZoom),
		);

		const success = await applyCameraConstraints({
			advanced: [{ zoom: clampedZoom }],
		});

		if (success) {
			setZoom(clampedZoom);
			console.log(`🔍 Zoom API Call: ${clampedZoom}x`);
		}
	};

	// Handle pan control
	const handlePanChange = async (newPan) => {
		if (!capabilities?.pan) {
			console.warn("⚠️ Pan not supported by this camera");
			setError("Pan not supported by this camera");
			return;
		}

		const clampedPan = Math.max(
			capabilities.pan.min || -180,
			Math.min(capabilities.pan.max || 180, newPan),
		);

		const success = await applyCameraConstraints({
			advanced: [{ pan: clampedPan }],
		});

		if (success) {
			setPan(clampedPan);
			console.log(`↔️ Pan API Call: ${clampedPan}°`);
		}
	};

	// Handle tilt control
	const handleTiltChange = async (newTilt) => {
		if (!capabilities?.tilt) {
			console.warn("⚠️ Tilt not supported by this camera");
			setError("Tilt not supported by this camera");
			return;
		}

		const clampedTilt = Math.max(
			capabilities.tilt.min || -90,
			Math.min(capabilities.tilt.max || 90, newTilt),
		);

		const success = await applyCameraConstraints({
			advanced: [{ tilt: clampedTilt }],
		});

		if (success) {
			setTilt(clampedTilt);
			console.log(`↕️ Tilt API Call: ${clampedTilt}°`);
		}
	};

	// Handle combined PTZ change
	const handlePTZChange = async (newPan, newTilt) => {
		const constraintsToApply = { advanced: [{}] };

		if (capabilities?.pan) {
			const clampedPan = Math.max(
				capabilities.pan.min || -180,
				Math.min(capabilities.pan.max || 180, newPan),
			);
			constraintsToApply.advanced[0].pan = clampedPan;
		}

		if (capabilities?.tilt) {
			const clampedTilt = Math.max(
				capabilities.tilt.min || -90,
				Math.min(capabilities.tilt.max || 90, newTilt),
			);
			constraintsToApply.advanced[0].tilt = clampedTilt;
		}

		if (Object.keys(constraintsToApply.advanced[0]).length > 0) {
			const success = await applyCameraConstraints(constraintsToApply);

			if (success) {
				if (capabilities?.pan) setPan(constraintsToApply.advanced[0].pan);
				if (capabilities?.tilt) setTilt(constraintsToApply.advanced[0].tilt);
				console.log(
					`🎯 PTZ API Call: Pan: ${constraintsToApply.advanced[0].pan}°, Tilt: ${constraintsToApply.advanced[0].tilt}°`,
				);
			}
		} else {
			console.warn("⚠️ Pan/Tilt not supported by this camera");
			setError("Pan/Tilt not supported by this camera");
		}
	};

	// Handle mouse drag for pan/tilt
	const handleMouseDown = (e) => {
		if (!capabilities?.pan && !capabilities?.tilt) return;
		setIsDragging(true);
		setDragStart({ x: e.clientX, y: e.clientY });
	};

	const handleMouseMove = (e) => {
		if (!isDragging || (!capabilities?.pan && !capabilities?.tilt)) return;

		const deltaX = e.clientX - dragStart.x;
		const deltaY = e.clientY - dragStart.y;

		const newPan = pan + deltaX * 0.5;
		const newTilt = tilt - deltaY * 0.5;

		setDragStart({ x: e.clientX, y: e.clientY });
		handlePTZChange(newPan, newTilt);
	};

	const handleMouseUp = () => {
		setIsDragging(false);
	};

	// Directional movement
	const handleDirectionalMove = (direction) => {
		const step = 5;
		let newPan = pan;
		let newTilt = tilt;

		switch (direction) {
			case "up":
				newTilt = tilt + step;
				handleTiltChange(newTilt);
				break;
			case "down":
				newTilt = tilt - step;
				handleTiltChange(newTilt);
				break;
			case "left":
				newPan = pan - step;
				handlePanChange(newPan);
				break;
			case "right":
				newPan = pan + step;
				handlePanChange(newPan);
				break;
			default:
				return;
		}

		console.log(`🎯 Directional Move API Call: ${direction}`);
	};

	// Preset positions
	const _presetPositions = [
		{ name: "Home", pan: 0, tilt: 0, zoom: 1 },
		{ name: "Left View", pan: -45, tilt: 0, zoom: 1.5 },
		{ name: "Right View", pan: 45, tilt: 0, zoom: 1.5 },
		{ name: "Overview", pan: 0, tilt: -20, zoom: 0.8 },
	];

	const goToPreset = async (preset) => {
		console.log(`📍 Preset API Call: Moving to ${preset.name}`);

		// Apply all constraints at once for smooth movement
		const constraintsToApply = { advanced: [{}] };

		if (capabilities?.pan) constraintsToApply.advanced[0].pan = preset.pan;
		if (capabilities?.tilt) constraintsToApply.advanced[0].tilt = preset.tilt;
		if (capabilities?.zoom) constraintsToApply.advanced[0].zoom = preset.zoom;

		if (Object.keys(constraintsToApply.advanced[0]).length > 0) {
			const success = await applyCameraConstraints(constraintsToApply);

			if (success) {
				if (capabilities?.pan) setPan(preset.pan);
				if (capabilities?.tilt) setTilt(preset.tilt);
				if (capabilities?.zoom) setZoom(preset.zoom);
			}
		}
	};

	// Reset to home position
	const resetPosition = () => {
		goToPreset({ name: "Home", pan: 0, tilt: 0, zoom: 1 });
		console.log("🏠 Reset API Call: Returning to home position");
	};

	// Recording functionality
	const mediaRecorderRef = useRef(null);
	const recordedChunksRef = useRef([]);

	// Handle video file from native camera (mobile)
	const handleVideoCapture = async (event) => {
		const file = event.target.files?.[0];
		if (!file) return;

		console.log("📹 Video captured from native camera:", file.name, file.type);

		// Upload the captured video
		if (uploadUrl) {
			const formData = new FormData();
			formData.append("file", file, file.name);

			try {
				const startTime = performance.now();
				const response = await fetch(uploadUrl, {
					method: "POST",
					body: formData,
				});
				const endTime = performance.now();
				const uploadTime = endTime - startTime;

				setLatency(uploadTime);
				console.log(`⏱️ Upload latency: ${uploadTime.toFixed(2)}ms`);

				if (response.ok) {
					console.log("✅ Video uploaded successfully");
				} else {
					console.error("❌ Video upload failed:", response.statusText);
					setError(`Video upload failed: ${response.statusText}`);
				}
			} catch (err) {
				console.error("❌ Error uploading video:", err);
				setError(`Error uploading video: ${err.message}`);
				setLatency(null);
			}
		}

		// Reset the input so the same file can be selected again
		event.target.value = "";
	};

	// Open native camera in video mode (mobile)
	const openNativeCamera = () => {
		if (fileInputRef.current) {
			fileInputRef.current.click();
		}
	};

	const startRecord = () => {
		// On mobile, open native camera app
		if (!isPC) {
			openNativeCamera();
			return;
		}

		// On desktop, use web-based recording
		if (!stream) {
			setError("Cannot start recording: No active camera stream");
			return;
		}

		recordedChunksRef.current = [];
		const options = { mimeType: "video/webm; codecs=vp9" };
		const mediaRecorder = new MediaRecorder(stream, options);

		mediaRecorder.ondataavailable = (event) => {
			if (event.data.size > 0) {
				recordedChunksRef.current.push(event.data);
			}
		};

		mediaRecorder.onstop = async () => {
			const blob = new Blob(recordedChunksRef.current, { type: "video/webm" });
			console.log("🎞️ Recording stopped, video blob created:", blob);

			// Upload the recorded video and measure latency
			if (uploadUrl) {
				const formData = new FormData();
				formData.append("file", blob, "recording.webm");

				try {
					const startTime = performance.now();
					const response = await fetch(uploadUrl, {
						method: "POST",
						body: formData,
					});
					const endTime = performance.now();
					const uploadTime = endTime - startTime;

					setLatency(uploadTime);
					console.log(`⏱️ Upload latency: ${uploadTime.toFixed(2)}ms`);

					if (response.ok) {
						console.log("✅ Video uploaded successfully");
					} else {
						console.error("❌ Video upload failed:", response.statusText);
						setError(`Video upload failed: ${response.statusText}`);
					}
				} catch (err) {
					console.error("❌ Error uploading video:", err);
					setError(`Error uploading video: ${err.message}`);
					setLatency(null);
				}
			}
		};

		mediaRecorder.start();
		mediaRecorderRef.current = mediaRecorder;
		setIsRecording(true);
		console.log("🔴 Recording started");
	};

	// Clear error after 5 seconds
	useEffect(() => {
		if (error) {
			const timer = setTimeout(() => setError(null), 5000);
			return () => clearTimeout(timer);
		}
	}, [error]);

	return (
		<div
			className={`lg:grid grid-cols-[14fr,5fr] gap-x-8 gap-y-2 text-color-Grey900 ${className}`}
		>
			{/* Hidden file input for native camera video capture (mobile) */}
			<input
				ref={fileInputRef}
				type="file"
				accept="video/*"
				capture="environment"
				onChange={handleVideoCapture}
				className="hidden"
			/>

			{/* Video Container */}
			<div className="hidden lg:inline-grid video-container">
				<div className="aspect-video relative">
					{isStreaming ? (
						<div
							className="w-full h-full cursor-move"
							onMouseDown={handleMouseDown}
							onMouseMove={handleMouseMove}
							onMouseUp={handleMouseUp}
							onMouseLeave={handleMouseUp}
						>
							<video
								ref={videoRef}
								className="w-full h-full object-cover"
								autoPlay
								muted
								playsInline
							/>
						</div>
					) : (
						<div className="w-full h-full flex items-center justify-center">
							<div className="text-center">
								<p className="text-xl mb-2">Camera Stream Offline</p>
								<p className="text-gray-400">Click “Start Camera” to begin</p>
								<p className="text-sm text-gray-500 mt-2">
									Allow camera access when prompted
								</p>
							</div>
						</div>
					)}
				</div>
			</div>
			{/* PTZ Control */}
			<div className="hidden lg:flex p-5 flex-col gap-y-5 bg-white border border-color-Grey100 rounded">
				<p className="text-lg font-medium">PTZ Control</p>

				{/* Zoom Controls */}
				<div>
					<p className="text-sm font-medium text-color-Grey700">Zoom Level</p>
					<div className="mt-4 flex items-center space-x-2">
						<button
							onClick={() => handleZoomChange(zoom - 0.1)}
							disabled={!capabilities?.zoom}
							className="flex items-center justify-center w-6 h-6 disabled:cursor-not-allowed"
						>
							<Icon iconName="magnifying_glass_minus" size="Medium" />
						</button>
						<RangeInput
							min={capabilities?.zoom?.min || 1}
							max={capabilities?.zoom?.max || 5}
							step="0.1"
							value={zoom}
							onChange={handleZoomChange}
							disabled={!capabilities?.zoom}
						/>
						<button
							onClick={() => handleZoomChange(zoom + 0.1)}
							disabled={!capabilities?.zoom}
							className="flex items-center justify-center w-6 h-6 disabled:cursor-not-allowed"
						>
							<Icon iconName="magnifying_glass_plus" size="Medium" />
						</button>
					</div>
					<p className="text-xs text-center">{zoom.toFixed(1)}x</p>
				</div>

				{/* Pan/Tilt Controls */}
				<div className="flex-1 flex flex-col">
					<p className="text-sm font-medium text-color-Grey700">Movement</p>
					{/* Directional pad */}
					<div className="w-fit mx-auto mb-auto grid grid-cols-3 gap-1">
						<div></div>
						<button
							onClick={() => handleDirectionalMove("up")}
							disabled={!capabilities?.tilt}
							className="w-14 h-14 flex items-center justify-center rounded-lg bg-color-Grey50 disabled:cursor-not-allowed"
						>
							<Icon iconName="arrow_up" size="XLarge" />
						</button>
						<div></div>

						<button
							onClick={() => handleDirectionalMove("left")}
							disabled={!capabilities?.pan}
							className="w-14 h-14 flex items-center justify-center rounded-lg bg-color-Grey50 disabled:cursor-not-allowed"
						>
							<Icon iconName="arrow_left" size="XLarge" />
						</button>
						<div></div>
						<button
							onClick={() => handleDirectionalMove("right")}
							disabled={!capabilities?.pan}
							className="w-14 h-14 flex items-center justify-center rounded-lg bg-color-Grey50 disabled:cursor-not-allowed"
						>
							<Icon iconName="arrow_right" size="XLarge" />
						</button>
						<div></div>
						<button
							onClick={() => handleDirectionalMove("down")}
							disabled={!capabilities?.tilt}
							className="w-14 h-14 flex items-center justify-center rounded-lg bg-color-Grey50 disabled:cursor-not-allowed"
						>
							<Icon iconName="arrow_down" size="XLarge" />
						</button>
						<div></div>
					</div>
					<BlockButton
						type="Secondary"
						width="100%"
						icon="arrow_clockwise"
						size="Large"
						disabled={
							!capabilities?.pan && !capabilities?.tilt && !capabilities?.zoom
						}
						onClick={resetPosition}
					>
						Reset View
					</BlockButton>
				</div>
			</div>
			{/* Stream Controls */}
			<div>
				<div className="hidden lg:flex items-center justify-between gap-x-5">
					<div className="flex-1 flex items-center space-x-2">
						<span
							className={`w-[5px] h-[5px] rounded-full ${isStreaming ? "bg-green-600" : "bg-red-600"}`}
						></span>
						<span
							className={`${isStreaming ? "text-green-600" : "text-red-600"}`}
						>
							{isStreaming ? "Connected" : "Disconnected"}
						</span>
					</div>
					<p className="text-sm font-medium text-color-Grey500">
						Resolution:{" "}
						<span className="text-sm font-medium text-color-Grey900">
							{getResolutionLabel(resolution.width, resolution.height)}
						</span>
					</p>
					<p className="text-sm font-medium text-color-Grey500">
						Latency:{" "}
						<span className="text-sm font-medium text-color-Grey900">
							{latency !== null ? `${latency.toFixed(0)}ms` : "N/A"}
						</span>
					</p>
				</div>
				<div className="mt-5 flex items-center justify-stretch lg:justify-center">
					<BlockButton
						type={isRecording ? "Critical" : "Primary"}
						width={isPC ? "180px" : "100%"}
						icon={isRecording ? "stop" : "play"}
						onClick={isRecording ? stopRecord : startRecord}
						className={`px-6 py-2 rounded font-semibold transition-all duration-200 ${
							isRecording
								? "bg-red-600 hover:bg-red-700 shadow-lg"
								: "bg-green-600 hover:bg-green-700 shadow-lg"
						}`}
					>
						{isRecording ? "Stop Recording" : "Start Recording"}
					</BlockButton>
				</div>
			</div>
		</div>
	);
};
